import { useLoaderData } from 'react-router';

import { templateType } from '~/routes/$/constants/template-types';
import { StandardLayout } from '~/routes/$/layouts/StandardLayout';
import { type SectionMap } from '~/types/template';

import { DynamicPageTemplate } from './components/DynamicPageTemplate/DynamicPageTemplate';
import { LandingPageTemplate } from './components/LandingPageTemplate/LandingPageTemplate';
import { SportsPageGridTemplate } from './components/SportsPageGridTemplate/SportsPageGridTemplate';
import { SportsPageHubTemplate } from './components/SportsPageHubTemplate/SportsPageHubTemplate';
import { type loader } from './loader.server';

export { meta } from './meta';
export { handle } from './handle';

export default function PageTemplateImpl() {
  const rootLoaderData = useLoaderData<typeof loader>();
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const { adUnitConfiguration, componentLoaderContext, gtmId, hasBannerAds, sections } = rootLoaderData;
  const sectionMap = sections as SectionMap;

  if (rootLoaderData.templateType === templateType.LANDING_PAGE) {
    const { backgroundImage, ctaSubText, ctaText, ctaUrl, h1Heading, introText } = rootLoaderData;
    return (
      <LandingPageTemplate
        backgroundImage={backgroundImage}
        componentLoaderContext={componentLoaderContext}
        ctaSubText={ctaSubText}
        ctaText={ctaText}
        ctaUrl={ctaUrl}
        expectedComponentLayout={rootLoaderData.expectedComponentLayout}
        gtmId={gtmId}
        h1Heading={h1Heading}
        introText={introText}
        sections={sectionMap}
        templateId={rootLoaderData.templateId}
        templateName={rootLoaderData.templateType}
        timings={rootLoaderData.timings}
      />
    );
  }

  if (rootLoaderData.templateType === templateType.SPORTS_PAGE_HUB) {
    return (
      <StandardLayout {...rootLoaderData}>
        <SportsPageHubTemplate
          adUnitConfiguration={adUnitConfiguration}
          componentLoaderContext={componentLoaderContext}
          expectedComponentLayout={rootLoaderData.expectedComponentLayout}
          gtmId={gtmId}
          hasBannerAds={hasBannerAds}
          sections={sectionMap}
          templateId={rootLoaderData.templateId}
          templateName={rootLoaderData.templateType}
          timings={rootLoaderData.timings}
        />
      </StandardLayout>
    );
  }

  if (rootLoaderData.templateType === templateType.SPORTS_PAGE_GRID) {
    return (
      <StandardLayout {...rootLoaderData}>
        <SportsPageGridTemplate
          adUnitConfiguration={adUnitConfiguration}
          componentLoaderContext={componentLoaderContext}
          expectedComponentLayout={rootLoaderData.expectedComponentLayout}
          gtmId={gtmId}
          hasBannerAds={hasBannerAds}
          sections={sectionMap}
          templateId={rootLoaderData.templateId}
          templateName={rootLoaderData.templateType}
          timings={rootLoaderData.timings}
        />
      </StandardLayout>
    );
  }

  // NGF-6128: footerData / navData / siteConfig reach StandardLayout via rootLoaderData (the loader
  // response now carries footerData / navData; siteConfig was already there), not the old
  // genericConfig spread.
  return (
    <StandardLayout {...rootLoaderData}>
      <DynamicPageTemplate
        adUnitConfiguration={adUnitConfiguration}
        componentLoaderContext={componentLoaderContext}
        expectedComponentLayout={rootLoaderData.expectedComponentLayout}
        gtmId={gtmId}
        hasBannerAds={hasBannerAds}
        sections={sectionMap}
        templateId={rootLoaderData.templateId}
        templateName={rootLoaderData.templateType}
        timings={rootLoaderData.timings}
      />
    </StandardLayout>
  );
}
